import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import { SeverityBadge } from "../components/SeverityBadge";
import {
  IconChat, IconPlay, IconSend, IconCopy, IconWhatsApp,
  IconDocument, IconRefresh, IconCheck, IconAlertTriangle,
} from "../components/icons";
import type { BiAskRequest, BiAskResponse, BiQuestionLogDto } from "@bi/types";

type Tab = "conversations" | "simulator" | "history" | "config";

const SUGGESTED_QUESTIONS = [
  "Como está meu faturamento hoje?",
  "Qual minha inadimplência?",
  "Tem algo preocupante hoje?",
  "Como está o caixa dos próximos 7 dias?",
  "Quais clientes mais faturaram esse mês?",
  "Quem são os maiores inadimplentes?",
  "Qual meu resultado estimado esse mês?",
  "Tem algum custo acima do normal?",
  "Como está meu fluxo de caixa?",
  "Qual o ticket médio dos CT-es este mês?",
];

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  answered:          { label: "Respondida",    className: "badge-green" },
  failed:            { label: "Erro",          className: "badge-red"   },
  pending:           { label: "Pendente",      className: "badge-amber" },
  interpreted:       { label: "Interpretada",  className: "badge-blue"  },
  needs_clarification: { label: "Clarificação", className: "badge-amber" },
};

// ─── Simulator ────────────────────────────────────────────────────────────────
function Simulator() {
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState<BiAskResponse | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const ask = useMutation({
    mutationFn: (body: BiAskRequest) => api.post<BiAskResponse>("/bi/ask", body),
    onSuccess: (data) => setResult(data),
  });

  const submit = () => {
    if (!question.trim()) return;
    setResult(null);
    ask.mutate({ question: question.trim() });
  };

  const useQuestion = (q: string) => {
    setQuestion(q);
    setResult(null);
    textareaRef.current?.focus();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Input */}
      <div className="lg:col-span-2 space-y-4">
        <div className="card p-5">
          <p className="text-sm font-semibold text-gray-900 mb-1">Simulador de Pergunta</p>
          <p className="text-xs text-gray-500 mb-4">
            Digite uma pergunta como se estivesse no WhatsApp. O sistema mostra como interpretaria e responderia.
          </p>
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), submit())}
              placeholder="Ex: Tem algo preocupante hoje?"
              rows={3}
              className="w-full rounded-xl border border-surface-border px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400 pr-16"
            />
            <button
              onClick={submit}
              disabled={!question.trim() || ask.isPending}
              className="absolute right-3 bottom-3 btn-primary py-2 px-3"
            >
              {ask.isPending ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <IconSend className="w-4 h-4" />
              )}
            </button>
          </div>
          {ask.isError && (
            <p className="mt-2 text-sm text-danger-600 flex items-center gap-1.5">
              <IconAlertTriangle className="w-4 h-4" />
              {(ask.error as Error).message}
            </p>
          )}
        </div>

        {/* Result */}
        {result && (
          <div className="card overflow-hidden">
            <div className="px-5 py-4 border-b border-surface-border bg-tm-50">
              <div className="flex items-center gap-2">
                <IconCheck className="w-4 h-4 text-tm-600" />
                <p className="text-sm font-semibold text-tm-800">Resposta simulada</p>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Answer */}
              <div className="bg-surface-muted rounded-xl px-4 py-3 text-sm text-gray-700 leading-relaxed">
                {result.answer}
              </div>

              {/* Interpretation */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Interpretação</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="bg-surface-muted rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-400">Intenção</p>
                    <p className="text-xs font-medium text-gray-800 mt-0.5">{result.interpretation.intent}</p>
                  </div>
                  {result.interpretation.metric && (
                    <div className="bg-surface-muted rounded-lg px-3 py-2">
                      <p className="text-xs text-gray-400">Métrica</p>
                      <p className="text-xs font-medium text-gray-800 mt-0.5">{result.interpretation.metric}</p>
                    </div>
                  )}
                  <div className="bg-surface-muted rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-400">Período</p>
                    <p className="text-xs font-medium text-gray-800 mt-0.5">{result.interpretation.period.type}</p>
                  </div>
                  <div className="bg-surface-muted rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-400">Confiança</p>
                    <p className="text-xs font-medium text-gray-800 mt-0.5">{(result.interpretation.confidence * 100).toFixed(0)}%</p>
                  </div>
                  <div className="bg-surface-muted rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-400">Gráfico</p>
                    <p className="text-xs font-medium text-gray-800 mt-0.5">{result.interpretation.wantsChart ? "Sim" : "Não"}</p>
                  </div>
                </div>
              </div>

              {/* Data */}
              {result.data && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Dados retornados</p>
                  <div className="flex items-center gap-4">
                    {result.data.primaryFormatted && (
                      <div className="text-center">
                        <p className="text-xl font-bold text-gray-900">{result.data.primaryFormatted}</p>
                        <p className="text-xs text-gray-400">Valor principal</p>
                      </div>
                    )}
                    {result.data.variationPct != null && (
                      <div className="text-center">
                        <p className={`text-xl font-bold ${result.data.variationPct >= 0 ? "text-tm-600" : "text-danger-600"}`}>
                          {result.data.variationPct >= 0 ? "+" : ""}{result.data.variationPct.toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-400">Variação</p>
                      </div>
                    )}
                  </div>

                  {result.data.breakdown?.length ? (
                    <div className="mt-3 space-y-1">
                      {result.data.breakdown.slice(0, 5).map((b, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{b.label}</span>
                          <span className="font-medium text-gray-900">{b.formatted}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Suggested questions */}
      <div className="space-y-4">
        <div className="card p-4">
          <p className="text-sm font-semibold text-gray-900 mb-3">Perguntas sugeridas</p>
          <div className="space-y-1.5">
            {SUGGESTED_QUESTIONS.map((q) => (
              <button
                key={q}
                onClick={() => useQuestion(q)}
                className="w-full text-left text-xs text-gray-700 px-3 py-2 rounded-lg bg-surface-muted border border-surface-border hover:border-tm-200 hover:bg-tm-50 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>

        <div className="card p-4 bg-tm-50 border-tm-100">
          <p className="text-xs font-semibold text-tm-800 mb-2 flex items-center gap-2">
            <IconWhatsApp className="w-4 h-4" />
            Enviar no WhatsApp real
          </p>
          <p className="text-xs text-tm-700 mb-3">Copie a pergunta e envie no WhatsApp para receber a resposta real.</p>
          <button
            onClick={() => {
              const url = `https://wa.me/5511999999999?text=${encodeURIComponent(question || "Como está meu faturamento hoje?")}`;
              window.open(url, "_blank");
            }}
            className="btn-primary w-full text-sm"
          >
            <IconWhatsApp className="w-4 h-4" />
            Abrir no WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── History ──────────────────────────────────────────────────────────────────
function History() {
  const { data, isLoading } = useQuery<BiQuestionLogDto[]>({
    queryKey: ["bi", "questions"],
    queryFn: () => api.get<BiQuestionLogDto[]>("/bi/questions"),
    staleTime: 30_000,
  });

  if (isLoading) return <div className="animate-pulse space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl" />)}</div>;

  if (!data?.length) return (
    <EmptyState
      icon={<IconChat className="w-7 h-7" />}
      title="Nenhuma conversa ainda"
      description="As perguntas feitas pelo WhatsApp ou chat web aparecem aqui."
    />
  );

  return (
    <div className="card overflow-hidden">
      <div className="divide-y divide-surface-border">
        {data.map((log) => {
          const st = STATUS_LABELS[log.status] ?? { label: log.status, className: "badge-gray" };
          return (
            <div key={log.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{log.question}</p>
                  {log.answer && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{log.answer}</p>
                  )}
                </div>
                <span className={st.className}>{st.label}</span>
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                <span>{fmtDateTime(log.createdAt)}</span>
                <span className="badge-gray">{log.channel}</span>
                {log.metricKey && <span>Métrica: {log.metricKey}</span>}
                {log.durationMs && <span>{log.durationMs}ms</span>}
                {log.userName && <span>por {log.userName}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Instances / Config ────────────────────────────────────────────────────────
function Config() {
  return (
    <div className="card p-8 text-center">
      <p className="text-sm font-medium text-gray-700">Configuração de instâncias Evolution disponível em</p>
      <a href="/configuracoes" className="mt-2 text-sm text-tm-600 hover:underline">Configurações → WhatsApp</a>
    </div>
  );
}

// ─── Status banner dinâmico ───────────────────────────────────────────────────

type InstanceStatus = "connected" | "disconnected" | "pending" | "error";

type WaInstance = {
  id: string; instanceName: string; evolutionBaseUrl: string;
  status: string; hasApiKey: boolean; webhookUrl: string | null; createdAt: string;
};

function StatusBanner({ onConfig }: { onConfig: () => void }) {
  const { data, isLoading, refetch, isRefetching } = useQuery<{ items: WaInstance[] }>({
    queryKey: ["whatsapp-instances"],
    queryFn: () => api.get("/whatsapp/instances"),
    staleTime: 60_000,
  });

  const instances = data?.items ?? [];
  const primary = instances[0] ?? null;
  const status = (primary?.status ?? "pending") as InstanceStatus;

  const BANNER_CFG: Record<InstanceStatus, { bg: string; dot: string; text: string; label: string; desc: string }> = {
    connected:    { bg: "bg-tm-50 border-tm-100",          dot: "bg-tm-500 animate-pulse", text: "text-tm-800",      label: "WhatsApp conectado",    desc: "respondendo perguntas automaticamente" },
    disconnected: { bg: "bg-danger-50 border-danger-200",  dot: "bg-danger-500",           text: "text-danger-800",  label: "WhatsApp desconectado", desc: "reconecte no painel da Evolution" },
    pending:      { bg: "bg-warning-50 border-warning-200",dot: "bg-warning-400",          text: "text-warning-800", label: "WhatsApp pendente",     desc: "aguardando autenticação" },
    error:        { bg: "bg-danger-50 border-danger-200",  dot: "bg-danger-600",           text: "text-danger-800",  label: "Erro na integração",    desc: "verifique as configurações da instância" },
  };
  const bannerConfig = BANNER_CFG[status] ?? BANNER_CFG.pending;

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 bg-surface-muted border border-surface-border px-4 py-3 rounded-xl animate-pulse">
        <div className="w-2 h-2 rounded-full bg-gray-300" />
        <div className="h-3 w-48 bg-gray-200 rounded" />
      </div>
    );
  }

  if (instances.length === 0) {
    return (
      <div className="flex items-center gap-3 bg-warning-50 border border-warning-200 px-4 py-3 rounded-xl">
        <IconAlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0" />
        <p className="text-sm text-warning-800">
          Nenhuma instância Evolution configurada.{" "}
          <button onClick={onConfig} className="font-semibold underline">Configurar agora</button>
        </p>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 border px-4 py-3 rounded-xl ${bannerConfig.bg}`}>
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bannerConfig.dot}`} />
      <p className={`text-sm ${bannerConfig.text}`}>
        <strong>{bannerConfig.label}</strong>
        {primary && ` — ${primary.instanceName} — `}
        {bannerConfig.desc}.
      </p>
      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={() => refetch()}
          disabled={isRefetching}
          className={`text-xs flex items-center gap-1 ${bannerConfig.text} hover:opacity-80`}
        >
          <IconRefresh className={`w-3 h-3 ${isRefetching ? "animate-spin" : ""}`} />
          Atualizar
        </button>
        <button onClick={onConfig} className={`text-xs underline ${bannerConfig.text}`}>
          Gerenciar
        </button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export function WhatsAppPage() {
  const [tab, setTab] = useState<Tab>("simulator");

  const TABS: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: "simulator",    label: "Simulador",  icon: IconPlay    },
    { id: "conversations", label: "Conversas", icon: IconChat    },
    { id: "history",      label: "Histórico",  icon: IconDocument },
    { id: "config",       label: "Configurar", icon: IconRefresh  },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="WhatsApp"
        subtitle="O grande diferencial da Inteligência SIAG — responda perguntas e receba alertas pelo WhatsApp"
        actions={
          <button className="btn-primary" onClick={() => setTab("config")}>
            <IconWhatsApp className="w-4 h-4" />
            Gerenciar instância
          </button>
        }
      />

      {/* Status banner dinâmico */}
      <StatusBanner onConfig={() => setTab("config")} />

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-muted p-1 rounded-xl border border-surface-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1 justify-center ${
              tab === t.id ? "bg-white text-gray-900 shadow-card" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "simulator"    && <Simulator />}
      {tab === "conversations" && (
        <EmptyState
          icon={<IconChat className="w-7 h-7" />}
          title="Conversas em tempo real"
          description="Esta funcionalidade estará disponível na próxima fase com WebSocket para atualização em tempo real."
          action={<button onClick={() => setTab("history")} className="btn-secondary">Ver histórico</button>}
        />
      )}
      {tab === "history" && <History />}
      {tab === "config"  && <Config />}
    </div>
  );
}
